<template>
  <Modal
    name="BULK_SEND_REJECTED_EMAIL_MODAL"
    width="500"
    :title="$t('appointment.confirmAppointment.sendRejectedEmailModal.title')"
    ref="sendEmails"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        class="mt-5 mb-7"
        v-show="isAppointmentLoading"
        indeterminate
        rounded
      />

      <div v-show="!isAppointmentLoading">
        <div v-show="!isSingleAppointment">
          <v-alert v-if="isUnableToSend" class="red lighten-5 mt-2 mb-6">
            <p class="body-2 red--text mb-2">
              {{ $t('appointment.confirmAppointment.sendRejectedEmailModal.unableToSendEmail') }}
            </p>
            <v-chip v-for="(item, index) in getAppointmentsUnableToSend" :key="index" class="mr-1 mb-1 red" x-small>
              <span class="white--text">
                {{ item.code }}
              </span>
            </v-chip>
            <p class="caption red--text mt-2">
              {{ $t('appointment.confirmAppointment.sendRejectedEmailModal.unableToSendEmailTip') }}
            </p>
          </v-alert>

          <p class="body-2 mb-2" v-if="canSendEmailRejections">
            {{ $t('appointment.confirmAppointment.sendRejectedEmailModal.sendEmailMessage', { quantity: getAppointmentsAbleToSend.length }) }}
          </p>
          <v-chip v-for="(item, index) in getAppointmentsAbleToSend" :key="index" class="mr-1 mb-1 orange" x-small>
            <span class="white--text">
              {{ item.code }}
            </span>
          </v-chip>
        </div>

        <!-- single appointment -->
        <ModalBulkSendRejectedEmailSingleAppointment
          v-if="isSingleAppointment"
          :isUnableToSendSingleAppointment="isUnableToSendSingleAppointment"
          :appointmentUnableToSend="appointmentUnableToSend"
          :appointmentAbleToSend="appointmentAbleToSend"
        />
        <!-- /single appointment -->
      </div>
    </template>

    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        class="mr-3"
        large
        text
      >
        {{ $t('appointment.confirmAppointment.sendRejectedEmailModal.cancel') }}
      </v-btn>
      <v-btn
        @click="sendEmails"
        :loading="loading"
        :disabled="!canSendEmailRejections"
        elevation="0"
        large
      >
        {{ $t('appointment.confirmAppointment.sendRejectedEmailModal.send') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { api } from '@/services'

import Modal from '../../app/Modal.vue'
import ModalBulkSendRejectedEmailSingleAppointment from './fragments/ModalBulkSendRejectedEmailSingleAppointment'

export default {
  name: 'ModalBulkSendRejectedEmail',

  components: {
    Modal,
    ModalBulkSendRejectedEmailSingleAppointment,
  },

  props: {
    selectedAppointments: {
      type: Array,
      required: true,
    },

    mapAction: {
      type: Boolean,
      required: false,
      default: true,
    }
  },

  watch: {
    appointment(appointment) {
      if (!appointment?.id) return

      if (this.canSendEmailRejection(appointment)) {
        this.appointmentAbleToSend.push(appointment)
      } else {
        this.appointmentUnableToSend.push(appointment)
      }
    }
  },

  computed: {
    ...mapGetters(['appointment', 'isAppointmentLoading']),

    isSingleAppointment() {
      return this.appointment && this.appointment.id
    },

    isUnableToSend() {
      return this.getAppointmentsUnableToSend && this.getAppointmentsUnableToSend.length > 0
    },

    isUnableToSendSingleAppointment() {
      return this.appointmentUnableToSend.length > 0
    },

    canSendEmailRejections() {
      if (this.isSingleAppointment) {
        return this.appointmentAbleToSend.length > 0
      }

      return this.getAppointmentsAbleToSend && this.getAppointmentsAbleToSend.length > 0
    },

    getAppointmentsUnableToSend() {
      return this.selectedAppointments.filter(appointment => !this.canSendEmailRejection(appointment))
    },

    getAppointmentsAbleToSend() {
      return this.selectedAppointments.filter(appointment => this.canSendEmailRejection(appointment))
    },
  },

  methods: {
    ...mapActions(['showSuccessMessage', 'showErrorMessage']),
    ...mapMutations(['closeAllModals', 'setAppointment']),

    createPayload() {
      if (this.isSingleAppointment) {
        return {
          appointment_ids: [this.appointment.id],
        }
      }

      return {
        appointment_ids: this.getAppointmentsAbleToSend.map(appointment => appointment.id),
      }
    },

    async sendEmails() {
      this.loading = true

      try {
        await api.post('admin/appointment/sendRejectedEmail', this.createPayload())
        this.showSuccessMessage(this.$t('appointment.confirmAppointment.sendRejectedEmailModal.successfullySent'))
        if (this.mapAction) {
          this.setAppointment({})
          this.$emit('refreshMap')
        }
        this.closeAllModals()
      } catch ({ data }) {
        this.showErrorMessage(data?.message || this.$t('appointment.confirmAppointment.sendRejectedEmailModal.error'))
      } finally {
        this.loading = false
      }
    },

    canSendEmailRejection(appointment) {
      return appointment.status == 'REJECTED'
    },

    resetSingleAppointmentRejection() {
      this.appointmentAbleToSend = []
      this.appointmentUnableToSend = []
    },
  },

  data() {
    return {
      loading: false,

      appointmentUnableToSend: [],
      appointmentAbleToSend: [],
    }
  },

  mounted() {
    if (this.mapAction) {
      this.$watch(
        () => this.$refs.sendEmails.dialog,
        (isOpen) => {
          if (isOpen) {
            if (this.selectedAppointments >= 1) {
              this.resetSingleAppointmentRejection()
              this.setAppointment({})
            }
          }

          if(!isOpen) {
            this.resetSingleAppointmentRejection()
            this.setAppointment({})
          }
        }
      )
    }
  }
}
</script>
